import React, { useContext, useEffect, useMemo, useState } from 'react';

import { formatMessage } from '@99designs/common/i18n';
import { claimBrief } from '__generated__/claimBrief';
import { launchBrief } from '__generated__/launchBrief';
import { Package } from '__generated__/Package';
import {
    DesignerSource,
    StructuredBriefErrorReason,
    StructuredBriefFieldsInput,
} from '__generated__/types';
import { updateBrief, updateBriefVariables } from '__generated__/updateBrief';
import { ValidationError } from '__generated__/ValidationError';
import { ApolloError } from 'apollo-client';
import ErrorBar from 'common/components/ErrorBar';
import Bugsnag from 'common/utils/Bugsnag';
import { Source } from 'launch/consts/signupSource';
import Artwork from 'projects/components/Artwork';
import { AuthContext, AuthFormProps } from 'projects/components/AuthContext';
import BriefContext from 'projects/components/BriefContext';
import BriefFooter, { Props as BriefFooterProps } from 'projects/components/BriefFooter';
import { LaunchState } from 'projects/components/LaunchModal';
import { FormErrors, ObservableForm } from 'projects/components/ObservableForm';
import { Props as StepFooterProps } from 'projects/components/StepFooter';
import StepHeader from 'projects/components/StepHeader';
import { MAX_TITLE_LENGTH } from 'projects/consts';
import { trackClickedNextButtonEvent } from 'projects/events';
import { StepState } from 'projects/util/step';
import { useApolloClient } from 'react-apollo';
import { useRouteMatch } from 'react-router-dom';
import { redirectLaunchedBrief } from '.';
import queries from './Brief.graphql';
import { history } from './BrowserRouter';

export interface Props<T> {
    schema: keyof StructuredBriefFieldsInput;
    stepState: StepState<T>;
    brief: BriefFooterProps<T>['brief'] & {
        id: string;
        userId: number;
        fields: T;
        designersWithSource: { source: DesignerSource }[];
    };
    viewer: {
        user: {
            id: number;
        } | null;
    };
    recommendedAuthModal?: AuthFormProps;
    directAuthModal?: AuthFormProps;
    children: (form: ObservableForm<T>) => React.ReactNode;
    refetch: (options: { id: string }) => Promise<any>;
    disableFooterInteraction?: boolean;
    totalAllowableDesigners?: number;
    disableContacts?: boolean;
    briefPackage?: Package | null;
}

function translateErrors(errors: ValidationError[]): FormErrors {
    const validationErrors: FormErrors = {};

    errors.forEach(({ field, reason }) => {
        validationErrors[field] = (() => {
            switch (reason) {
                case StructuredBriefErrorReason.TOO_LONG:
                    switch (field) {
                        case 'title':
                            return formatMessage(
                                __(
                                    'Woah, the name of your project is too long. Please keep it less than {maxSize} characters.'
                                ),
                                { maxSize: MAX_TITLE_LENGTH }
                            );
                        default:
                            return __('This doesn’t look quite right. Please try again.');
                    }
                case StructuredBriefErrorReason.BLANK:
                    switch (field) {
                        case 'title':
                            return __("Don't forget to name your project.");
                        case 'category':
                            return __("Don't forget to choose a category.");
                        case 'customCategory':
                            return __("Don't forget to enter your own category.");
                        case 'description':
                            return __("Don't forget to describe your project.");
                        default:
                            return __('This field is required.');
                    }
                case StructuredBriefErrorReason.INVALID:
                    return __('This doesn’t look quite right. Please try again.');
                default:
                    Bugsnag.notify({
                        name: 'MissingValidationError',
                        message: `Missing validation error for "${reason}:${field}"`,
                    });
                    return '';
            }
        })();
    });

    return validationErrors;
}

function Error({
    error,
    onDismiss,
}: {
    error: ApolloError | Error | undefined;
    onDismiss: () => void;
}) {
    if (!error) {
        return null;
    }

    let message = error.message;
    if (process.env.NODE_ENV === 'production') {
        message = __('Uh oh... Please try again or refresh the page.');
    }

    return <ErrorBar onDismiss={onDismiss}>{message}</ErrorBar>;
}

export default function Brief<T>(props: Props<T>) {
    const [launchState, setLaunchState] = useState<LaunchState | undefined>();
    const [nextInProgress, setNextInProgress] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [error, setError] = useState<ApolloError | Error | undefined>();
    const {
        stepState: step,
        disableFooterInteraction,
        brief,
        totalAllowableDesigners = 5,
        disableContacts = false,
        briefPackage,
    } = props;
    const client = useApolloClient();
    const { login } = useContext(AuthContext);

    const defaultAuthModalRecommended: AuthFormProps = {
        source: Source.PROJECTS_LAUNCH,
        signupHeading: __("You're almost there..."),
        signupMessage: <p>{__('Sign up to see your designer recommendations.')}</p>,
        postLogin: resetStore,
    };

    const defaultAuthModalDirect: AuthFormProps = {
        source: Source.PROJECTS_LAUNCH,
        signupHeading: __("You're almost there..."),
        signupMessage: <p>{__('Sign up to kick off your project.')}</p>,
    };

    const route = useRouteMatch();

    const form = useMemo(
        () =>
            new ObservableForm<T>(props.brief.fields, {
                onSave: save,
                autosaveDelay: 1000,
            }),
        [route]
    );

    useEffect(() => {
        if (window.analytics) {
            window.analytics.page();
        }
    }, [route]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [props.stepState.number]);

    useEffect(() => {
        if (props.brief.projectIds.length > 0) {
            redirectLaunchedBrief(props.brief);
        }
    }, []);

    useEffect(() => {
        // On load, if the current step requires authentication, try to claim the brief.
        // This is used in the 2FA flow which will redirect the user, authenticate them,
        // and then redirect back to the next step, before triggering this code.
        const {
            stepState: step,
            viewer: { user },
        } = props;

        if (user && step.currentStep().isAuthRequired) {
            void claimIfUnclaimed();
        }
    }, []);

    async function resetStore() {
        await client.resetStore();
    }

    async function save(changes: Partial<T>): Promise<FormErrors> {
        try {
            setSaveInProgress(true);

            const res = await client.mutate<updateBrief, updateBriefVariables>({
                mutation: queries.updateBrief,
                variables: {
                    id: props.brief.id,
                    fields: { [props.schema]: changes },
                },
            });

            form.setData({
                ...form.data,
                ...changes,
            });

            // Filter validation errors down to the fields on the current step
            const currentStepFields = props.stepState.currentStep().fields || [];
            const errors = res.data!.brief.errors.filter(e =>
                currentStepFields.includes(e.field as keyof T)
            );

            return translateErrors(errors);
        } catch (error) {
            setError(error);
            return {};
        } finally {
            setSaveInProgress(false);
        }
    }

    async function claimIfUnclaimed() {
        if (!props.brief.userId) {
            await client.mutate<claimBrief>({
                mutation: queries.claimBrief,
                variables: { id: props.brief.id },
            });
        }
    }

    async function userInitiatedSave() {
        await claimIfUnclaimed();
        try {
            await form.save();
        } catch (error) {
            setError(error);
        }

        scrollToFirstError();
    }

    function scrollToFirstError() {
        const firstError = form.firstError();
        if (!firstError) {
            return;
        }
        form.scrollTo(firstError);
    }

    async function handleNextClick() {
        setNextInProgress(true);

        try {
            trackClickedNextButtonEvent({ id: props.brief.id });
            try {
                await form.save();
                await props.refetch({ id: props.brief.id });
            } catch (error) {
                setError(error);
            }

            if (form.hasErrors()) {
                scrollToFirstError();
                return;
            }

            const {
                stepState: step,
                viewer: { user },
                recommendedAuthModal = defaultAuthModalRecommended,
            } = props;
            if (!user && step.nextRequiresAuth()) {
                if (
                    !(await showAuthModal({
                        ...defaultAuthModalRecommended,
                        ...recommendedAuthModal,
                    }))
                ) {
                    return;
                }

                await claimIfUnclaimed();

                history.push(step.nextUrl());
            } else if (!error) {
                if (user) {
                    await claimIfUnclaimed();
                }

                history.push(step.nextUrl());
            }
        } finally {
            setNextInProgress(false);
        }
    }

    async function showAuthModal(authFormProps: AuthFormProps) {
        if (props.viewer.user != null) {
            return true;
        }
        return login({ ...authFormProps, target: step.nextUrl() });
    }

    async function launch() {
        const { directAuthModal = defaultAuthModalDirect } = props;
        let launchTimeout;
        try {
            setNextInProgress(true);

            if (await showAuthModal({ ...defaultAuthModalDirect, ...directAuthModal })) {
                await userInitiatedSave();

                // We've had some reports of launches stalling without an error. This is currently
                // here to give us a picture of how many users are being affected by this.
                launchTimeout = setTimeout(
                    () =>
                        Bugsnag.notify(
                            {
                                name: 'Launch',
                                message: 'Launch timeout',
                            },
                            { metaData: { brief: this.brief.id } }
                        ),
                    15000
                );

                const res = await client.mutate<launchBrief>({
                    mutation: queries.launchBrief,
                    variables: { id: props.brief.id },
                });

                if (res) {
                    setLaunchState(LaunchState.Success);
                }
            }
        } catch (error) {
            Bugsnag.notify(
                { name: 'Launch', message: 'Launch error' },
                { metaData: { brief: brief.id, error } }
            );
            setLaunchState(LaunchState.Error);
        } finally {
            if (launchTimeout) {
                clearTimeout(launchTimeout);
            }
            setNextInProgress(false);
        }
    }

    const stepFooterProps: StepFooterProps<T> = {
        step,
        isAuthenticated: brief.userId > 0,
        nextInProgress,
        nextAction: step.hasStepAfter() ? handleNextClick : launch,
        nextActionLabel: step.nextActionLabel(),
        disabled: nextInProgress,
        launchAction: launch,
    };

    return (
        <BriefContext.Provider
            value={{
                brief: brief,
                package: briefPackage,
                schema: props.schema,
                totalAllowableDesigners: totalAllowableDesigners,
                disableContacts,
            }}
        >
            {error && <Error error={error} onDismiss={() => setError(undefined)} />}
            <Artwork step={step} />
            <StepHeader step={step} designers={brief.designers} briefPackage={briefPackage} />
            {props.children(form)}
            <BriefFooter
                stepFooterProps={stepFooterProps}
                launchState={launchState}
                brief={brief}
                saving={nextInProgress || saveInProgress}
                disableFooterInteraction={disableFooterInteraction}
            />
        </BriefContext.Provider>
    );
}
